import {ChangeDetectionStrategy, Component, computed, input, signal} from '@angular/core';
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {InputText} from "primeng/inputtext";
import {FloatLabel} from "primeng/floatlabel";
import {InputGroup} from "primeng/inputgroup";
import {InputGroupAddon} from "primeng/inputgroupaddon";
import {ButtonDirective, ButtonIcon} from "primeng/button";
import {createVisibleErrorMessage} from "../form-error/form-error.utils";

type Config = {
  type: "text";
  autocomplete?: "given-name" | "family-name" | 'street-address' | 'postal-code';
} | "email" | {
  type: "password";
  autocomplete?: "current-password" | "new-password";
};

@Component({
  selector: 'homey-text-input',
  imports: [
    ReactiveFormsModule,
    InputText,
    FloatLabel,
    InputGroup,
    InputGroupAddon,
    ButtonDirective,
    ButtonIcon
  ],
  templateUrl: './text-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class TextInputComponent {
  public readonly inputFormControl = input.required<FormControl<string>>();
  public readonly label = input.required<string>();
  public readonly inputId = input.required<string>();
  public readonly type = input.required<Config>();
  public readonly errorMessage = input<string | Record<string, string>>();
  public readonly showPasswordToggle = input(false);

  protected readonly passwordVisible = signal(false);
  protected readonly visibleErrorMessage = createVisibleErrorMessage(this.inputFormControl, this.errorMessage);

  protected readonly autocomplete = computed(
    () => {
      const type = this.type();
      if (typeof type === "string") {
        switch (type) {
          case "email":
            return "email";
        }
      } else {
        return type.autocomplete;
      }
    }
  );

  protected readonly fieldType = computed(
    () => {
      const type = this.type();
      if (typeof type === "string") {
        return type;
      } else {
        return type.type;
      }
    }
  );

  protected readonly passwordType = computed(() => {
    if (this.fieldType() !== 'password' || !this.showPasswordToggle()) {
      return this.fieldType();
    }

    return this.passwordVisible() ? 'text' : 'password';
  });

  togglePasswordVisibility() {
    this.passwordVisible.update((current) => !current);
  }
}
